import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiLetterBoxed } from '../utils/api';
import PageSEO from '../components/PageSEO';
import SeoContent from '../components/SeoContent';

/* Square geometry: an inner square of side S, with a MARGIN around it so the
 * letter dots (which sit on the edges) are never clipped. */
const S = 300;
const MARGIN = 30;
const TOTAL = S + 2 * MARGIN;
const FRACS = [0.2, 0.5, 0.8];

// Map each side (0=top,1=right,2=bottom,3=left) + slot index → an {x,y} point.
function pointFor(sideIdx, slot) {
  const f = FRACS[slot];
  switch (sideIdx) {
    case 0: return { x: MARGIN + f * S, y: MARGIN };          // top
    case 1: return { x: MARGIN + S,     y: MARGIN + f * S };  // right
    case 2: return { x: MARGIN + f * S, y: MARGIN + S };      // bottom
    default: return { x: MARGIN,        y: MARGIN + f * S };  // left
  }
}

const font = 'Nunito, sans-serif';

export default function LetterBoxed() {
  const [phase, setPhase]   = useState('idle');   // idle | loading | playing | won | error
  const [puzzle, setPuzzle] = useState(null);
  const [current, setCurrent] = useState('');     // word being built
  const [words, setWords]   = useState([]);       // submitted words (uppercase)
  const [message, setMessage] = useState('');
  const [showSol, setShowSol] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [copied, setCopied] = useState(false);

  // Lookups derived from the current puzzle.
  const { sideOf, posOf, wordSet } = useMemo(() => {
    if (!puzzle) return { sideOf: {}, posOf: {}, wordSet: new Set() };
    const sideOf = {}, posOf = {};
    puzzle.sides.forEach((side, si) =>
      side.forEach((ch, slot) => { sideOf[ch] = si; posOf[ch] = pointFor(si, slot); })
    );
    return { sideOf, posOf, wordSet: new Set(puzzle.words.map(w => w.toLowerCase())) };
  }, [puzzle]);

  const usedLetters = useMemo(() => {
    const s = new Set();
    words.forEach(w => w.split('').forEach(c => s.add(c)));
    return s;
  }, [words]);

  const lastLetter = words.length ? words[words.length - 1].slice(-1) : '';
  const carry = lastLetter;                       // each new word must start here

  const flashMessage = (msg) => {
    setMessage(msg);
    setShaking(true);
    setTimeout(() => setShaking(false), 400);
  };

  const load = useCallback(async () => {
    setPhase('loading');
    setWords([]); setCurrent(''); setMessage(''); setShowSol(false); setCopied(false);
    try {
      const { data } = await apiLetterBoxed();
      setPuzzle(data);
      setPhase('playing');
    } catch (_) {
      setPhase('error');
    }
  }, []);

  const addLetter = useCallback((L) => {
    if (phase !== 'playing') return;
    if (!(L in sideOf)) return;                   // not a box letter
    setMessage('');
    setCurrent(cur => {
      if (cur === '') return L;                   // first letter (carry already sits here)
      const prev = cur[cur.length - 1];
      if (sideOf[L] === sideOf[prev]) {
        flashMessage('You can’t use two letters from the same side in a row.');
        return cur;
      }
      return cur + L;
    });
  }, [phase, sideOf]);

  const del = useCallback(() => {
    setCurrent(cur => (cur.length > carry.length ? cur.slice(0, -1) : cur));
  }, [carry]);

  const submit = useCallback(() => {
    if (phase !== 'playing') return;
    const w = current;
    if (w.length < 3) { flashMessage('Words must be at least 3 letters.'); return; }
    if (words.includes(w)) { flashMessage('You already used that word.'); return; }
    if (!wordSet.has(w.toLowerCase())) { flashMessage('Not a word in the dictionary.'); return; }

    const nextWords = [...words, w];
    setWords(nextWords);
    setCurrent(w.slice(-1));                       // carry last letter into next word

    const covered = new Set();
    nextWords.forEach(x => x.split('').forEach(c => covered.add(c)));
    if (covered.size === 12) { setPhase('won'); setMessage(''); setCurrent(''); }
    else setMessage('');
  }, [phase, current, words, wordSet]);

  // Keyboard support while playing.
  useEffect(() => {
    if (phase !== 'playing') return;
    const onKey = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
      else if (e.key === 'Backspace') { e.preventDefault(); del(); }
      else if (/^[a-zA-Z]$/.test(e.key)) { addLetter(e.key.toUpperCase()); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, submit, del, addLetter]);

  const restart = () => { setWords([]); setCurrent(''); setMessage(''); setPhase('playing'); };

  const shareResult = async () => {
    const text =
      `🔤 Letter Boxed — solved in ${words.length} word${words.length !== 1 ? 's' : ''}!\n` +
      `${words.join(' → ')}\n` +
      `Play free at https://wordzingo.com/letter-boxed`;
    try {
      if (navigator.share) await navigator.share({ text });
      else { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    } catch (_) { /* user dismissed */ }
  };

  /* ── SVG path for the word currently being drawn ── */
  const currentPath = current.split('').map(c => posOf[c]).filter(Boolean);

  const dot = (ch) => {
    const p = posOf[ch];
    const used = usedLetters.has(ch);
    const isTip = current.length > 0 && current[current.length - 1] === ch;
    return (
      <button
        key={ch}
        onClick={() => addLetter(ch)}
        style={{
          position: 'absolute', left: p.x, top: p.y, transform: 'translate(-50%,-50%)',
          width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
          fontFamily: font, fontWeight: 900, fontSize: '1.15rem',
          border: isTip ? '3px solid #f59e0b' : '3px solid #1f2937',
          background: isTip ? '#f59e0b' : used ? '#1f2937' : '#fff',
          color: isTip ? '#1f2937' : used ? '#fff' : '#1f2937',
          transition: 'all 0.12s', zIndex: 2, padding: 0,
        }}
      >
        {ch}
      </button>
    );
  };

  const bigBtn = {
    fontFamily: font, fontWeight: 800, fontSize: '1.1rem', color: '#fff',
    background: 'linear-gradient(90deg,#7c3aed,#db2777)', border: 'none',
    borderRadius: 50, padding: '0.8rem 2rem', cursor: 'pointer',
  };
  const ctrlBtn = (bg) => ({
    fontFamily: font, fontWeight: 800, fontSize: '0.95rem', color: '#fff',
    background: bg, border: 'none', borderRadius: 12, padding: '0.6rem 1.1rem', cursor: 'pointer',
  });

  return (
    <div className="game-page">
      <PageSEO page="letter-boxed" />

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 1rem' }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        {/* Header */}
        <div className="text-center mb-3">
          <h1 style={{
            fontFamily: font, fontWeight: 900, fontSize: 'clamp(2rem, 6vw, 2.8rem)', margin: 0,
            background: 'linear-gradient(90deg,#7c3aed,#db2777)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            🔤 Letter Boxed
          </h1>
          <p style={{ color: '#6b7280', fontFamily: font, margin: '0.3rem 0 0' }}>
            Link the letters into words and use all 12 sides of the box.
          </p>
        </div>

        {/* ── IDLE / ERROR ── */}
        {(phase === 'idle' || phase === 'error' || phase === 'loading') && (
          <div className="text-center">
            <div style={{ background: '#fff', borderRadius: 20, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
              <h3 style={{ fontFamily: font, fontWeight: 800, color: '#7c3aed', marginBottom: '1.25rem' }}>How to Play</h3>
              <div className="row g-3 text-start">
                {[
                  ['🔠', 'Build words from the 12 letters around the box (3+ letters each).'],
                  ['↔️', 'Consecutive letters must come from different sides of the box.'],
                  ['🔗', 'Each new word must start with the last letter of the word before it.'],
                  ['♻️', 'Letters can be reused as often as you like across words.'],
                  ['🎯', 'Goal: use every one of the 12 letters — in as few words as possible.'],
                  ['⭐', 'Par is 2 words. Can you match it?'],
                ].map(([icon, text]) => (
                  <div key={text} className="col-12 col-sm-6">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>
                      <span style={{ fontFamily: font, color: '#374151', fontSize: '0.95rem' }}>{text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {phase === 'error' && (
              <p style={{ color: '#dc2626', fontFamily: font, marginBottom: '1rem' }}>
                Couldn’t load a puzzle. Check your connection and try again.
              </p>
            )}
            <button onClick={load} disabled={phase === 'loading'} style={bigBtn}>
              {phase === 'loading' ? 'Loading…' : '▶ New Puzzle'}
            </button>
          </div>
        )}

        {/* ── PLAYING / WON ── */}
        {(phase === 'playing' || phase === 'won') && puzzle && (
          <>
            {/* Current word / status line */}
            <div style={{
              textAlign: 'center', minHeight: 40, fontFamily: font, fontWeight: 900,
              fontSize: '1.6rem', letterSpacing: '0.1em', color: '#1f2937',
              transform: shaking ? 'translateX(0)' : 'none',
              animation: shaking ? 'lb-shake 0.4s' : 'none',
            }}>
              {current || <span style={{ color: '#cbd5e1' }}>{phase === 'won' ? '🎉' : 'Tap letters…'}</span>}
            </div>
            <div style={{ textAlign: 'center', minHeight: 22, color: message ? '#dc2626' : '#9ca3af', fontFamily: font, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              {message || `${usedLetters.size} / 12 letters used`}
            </div>

            {/* The box */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <div style={{ position: 'relative', width: TOTAL, height: TOTAL }}>
                {/* square border */}
                <div style={{ position: 'absolute', left: MARGIN, top: MARGIN, width: S, height: S, border: '2px solid #e5e7eb', borderRadius: 8 }} />
                {/* connecting lines */}
                <svg width={TOTAL} height={TOTAL} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                  {currentPath.slice(1).map((p, i) => (
                    <line key={i} x1={currentPath[i].x} y1={currentPath[i].y} x2={p.x} y2={p.y}
                          stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
                  ))}
                </svg>
                {/* letter dots */}
                {puzzle.sides.flat().map(dot)}
              </div>
            </div>

            {/* Controls */}
            {phase === 'playing' && (
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
                <button onClick={del} style={ctrlBtn('#6b7280')}>⌫ Delete</button>
                <button onClick={submit} style={ctrlBtn('#7c3aed')}>↵ Enter</button>
                <button onClick={restart} style={ctrlBtn('#0891b2')}>↺ Restart</button>
                <button onClick={load} style={ctrlBtn('#db2777')}>🎲 New</button>
              </div>
            )}

            {/* Won banner */}
            {phase === 'won' && (
              <div style={{ background: 'linear-gradient(135deg,#7c3aed,#db2777)', color: '#fff', borderRadius: 16, padding: '1.25rem', textAlign: 'center', marginBottom: '1rem' }}>
                <div style={{ fontFamily: font, fontWeight: 900, fontSize: '1.4rem' }}>
                  🎉 Solved in {words.length} word{words.length !== 1 ? 's' : ''}!
                </div>
                <div style={{ fontFamily: font, opacity: 0.9, marginTop: 4 }}>
                  {words.length <= 2 ? 'Par or better — brilliant!' : `Par is 2 — can you do it shorter?`}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem' }}>
                  <button onClick={shareResult} style={{ ...ctrlBtn('rgba(255,255,255,0.25)') }}>
                    {copied ? '✓ Copied!' : '📤 Share'}
                  </button>
                  <button onClick={load} style={ctrlBtn('rgba(255,255,255,0.25)')}>🎲 New Puzzle</button>
                </div>
              </div>
            )}

            {/* Words played */}
            {words.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, padding: '0.9rem 1.1rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '1rem' }}>
                <div style={{ fontFamily: font, fontWeight: 800, color: '#7c3aed', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  Your words ({words.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {words.map((w, i) => (
                    <span key={i} style={{ fontFamily: font, fontWeight: 700, background: '#ede9fe', color: '#5b21b6', borderRadius: 8, padding: '0.25rem 0.6rem', fontSize: '0.9rem' }}>{w}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Hint */}
            {phase === 'playing' && (
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                {showSol ? (
                  <p style={{ fontFamily: font, color: '#6b7280', fontSize: '0.9rem' }}>
                    💡 One 2-word solution: <strong>{puzzle.solution.join(' → ')}</strong>
                  </p>
                ) : (
                  <button onClick={() => setShowSol(true)} style={{ ...ctrlBtn('#e5e7eb'), color: '#374151' }}>
                    💡 Show a solution
                  </button>
                )}
              </div>
            )}
          </>
        )}

        <style>{`@keyframes lb-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}`}</style>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 1rem' }}>
        <SeoContent game="letter-boxed" />
      </div>
    </div>
  );
}
